/**
 * SakDetaljer - Detaljert visning av en sak med filer, tidslinje og frister
 */

import React, { useState, useEffect } from 'react';
import crmApi from '../api/crm';
import OppgaveListe from '../components/OppgaveListe';
import FakturaRegistrering from '../components/FakturaRegistrering';
import BevisOversikt from '../components/BevisOversikt';
import Mangelsregister from '../components/Mangelsregister';
import KategoriAdmin from '../components/KategoriAdmin';
import BevisDialog from '../components/BevisDialog';
import EmailListe from '../components/EmailListe';
import TodoListe from '../components/TodoListe';
import SaksTimeline from '../components/SaksTimeline';
import OppgaverGruppert from '../components/OppgaverGruppert';
import DokumentVersjoner from '../components/DokumentVersjoner';
import OppdragsbekreftelseSkjema from '../components/OppdragsbekreftelseSkjema';
import { genererPDFFraHTMLString } from '../utils/pdfGenerator';
import './SakDetaljer.css';

export default function SakDetaljer({ sakId }) {
  const [sak, setSak] = useState(null);
  const [filer, setFiler] = useState([]);
  const [tidslinje, setTidslinje] = useState([]);
  const [frister, setFrister] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('tidslinje');
  const [uploadingFile, setUploadingFile] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [rettshjelp, setRettshjelp] = useState(null);
  const [fakturaer, setFakturaer] = useState([]);
  const [showRettshjelpModal, setShowRettshjelpModal] = useState(false);
  const [rettshjelpForm, setRettshjelpForm] = useState({});
  const [forsikring, setForsikring] = useState(null);
  const [visFakturaModal, setVisFakturaModal] = useState(false);
  const [visKategoriAdmin, setVisKategoriAdmin] = useState(false);
  const [visBevisDialog, setVisBevisDialog] = useState(false);
  const [aktivtDokument, setAktivtDokument] = useState(null);
  const [visOppdragsbekreftelseModal, setVisOppdragsbekreftelseModal] = useState(false);
  const [genererOppdragsbekreftelseLoading, setGenererOppdragsbekreftelseLoading] = useState(false);
  const [visStatusrapportModal, setVisStatusrapportModal] = useState(false);
  const [genererStatusrapportLoading, setGenererStatusrapportLoading] = useState(false);
  const [budsjettStatus, setBudsjettStatus] = useState(null);
  const [eksportererTripletex, setEksportererTripletex] = useState(false);
  const [dokumentHistorikk, setDokumentHistorikk] = useState([]);
  const [lasterDokumentHistorikk, setLasterDokumentHistorikk] = useState(false);
  const [visStatusEndringModal, setVisStatusEndringModal] = useState(false);
  const [statusEndringForm, setStatusEndringForm] = useState({
    nyStatus: '',
    avslutningsgrunn: '',
    avsluttingsdato: new Date().toISOString().split('T')[0],
    genererAvslutningsbrev: false,
    initierSluttoppgjor: false,
    kommentar: ''
  });
  const [opplastingOppdragsbekreftelse, setOpplastingOppdragsbekreftelse] = useState(false);
  const [auditLogs, setAuditLogs] = useState([]);
  const [lasterAuditLogs, setLasterAuditLogs] = useState(false);
  const [visVersjonerModal, setVisVersjonerModal] = useState(false);
  const [valgFilForVersjoner, setValgFilForVersjoner] = useState(null);

  useEffect(() => {
    if (sakId) {
      lastSakData();
      lastDokumentHistorikk();
      lastAuditLogs();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sakId]);

  // Initialiser editForm med oppfølgingsfelter når sak lastes
  useEffect(() => {
    if (sak) {
      setEditForm(prev => ({
        ...prev,
        neste_oppfolging_klient_dato: sak.neste_oppfolging_klient_dato || '',
        neste_oppfolging_klient_type: sak.neste_oppfolging_klient_type || '',
        neste_oppfolging_klient_beskrivelse: sak.neste_oppfolging_klient_beskrivelse || '',
        neste_oppfolging_motpart_dato: sak.neste_oppfolging_motpart_dato || '',
        neste_oppfolging_motpart_type: sak.neste_oppfolging_motpart_type || '',
        neste_oppfolging_motpart_beskrivelse: sak.neste_oppfolging_motpart_beskrivelse || ''
      }));
    }
  }, [sak]);

  const lastDokumentHistorikk = async () => {
    try {
      setLasterDokumentHistorikk(true);
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${crmApi.baseUrl}/api/oppdragsbekreftelse/historikk/${sakId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Kunne ikke hente dokumenthistorikk');
      }

      const result = await response.json();
      setDokumentHistorikk(result.data || []);
    } catch (error) {
      console.error('Feil ved henting av dokumenthistorikk:', error);
    } finally {
      setLasterDokumentHistorikk(false);
    }
  };

  const lastAuditLogs = async () => {
    try {
      setLasterAuditLogs(true);
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${crmApi.baseUrl}/api/saker/${sakId}/audit`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Kunne ikke hente aktivitetslogg');
      }

      const result = await response.json();
      setAuditLogs(result.audit || []);
    } catch (error) {
      console.error('Feil ved henting av audit log:', error);
    } finally {
      setLasterAuditLogs(false);
    }
  };

  const lastSakData = async () => {
    try {
      setLoading(true);

      // Hent sakdata parallelt (med error handling for manglende endpoints)
      const token = localStorage.getItem('access_token');
      const [sakData, filerData, tidslinjeData, fristerData, fakturaerData, forsikringData] = await Promise.all([
        crmApi.getSak(sakId),
        crmApi.getSakFiler(sakId).catch(() => []),
        crmApi.getTidslinje(sakId).catch(() => ({tidslinje: []})),
        crmApi.getFrister(sakId).catch(() => ({frister: []})),
        fetch(`${crmApi.baseUrl}/api/saker/${sakId}/fakturaer`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }).then(r => r.ok ? r.json() : []).catch(() => []),
        fetch(`${crmApi.baseUrl}/api/forsikringsdekning`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }).then(r => r.ok ? r.json() : {dekninger: []}).catch(() => ({dekninger: []}))
      ]);

      console.log('=== DATA LOADED ===');
      console.log('sakData:', sakData);
      console.log('filerData:', filerData, 'isArray:', Array.isArray(filerData));
      console.log('tidslinjeData:', tidslinjeData, 'isArray:', Array.isArray(tidslinjeData?.tidslinje));
      console.log('fristerData:', fristerData, 'isArray:', Array.isArray(fristerData?.frister));
      console.log('fakturaerData:', fakturaerData, 'isArray:', Array.isArray(fakturaerData));

      setSak(sakData);
      setFiler(Array.isArray(filerData) ? filerData : []);
      setTidslinje(Array.isArray(tidslinjeData?.tidslinje) ? tidslinjeData.tidslinje : []);
      setFrister(Array.isArray(fristerData?.frister) ? fristerData.frister : []);
      setFakturaer(Array.isArray(fakturaerData) ? fakturaerData : (fakturaerData?.fakturaer || []));

      console.log('=== STATE SET ===');
      console.log('filer:', Array.isArray(filerData) ? filerData : []);
      console.log('tidslinje:', Array.isArray(tidslinjeData?.tidslinje) ? tidslinjeData.tidslinje : []);
      console.log('frister:', Array.isArray(fristerData?.frister) ? fristerData.frister : []);
      console.log('fakturaer:', Array.isArray(fakturaerData) ? fakturaerData : (fakturaerData?.fakturaer || []));

      // Rettshjelp ligger nå direkte på sak-objektet
      const harRettshjelp = sakData.rettshjelp_bkf_selskap_id || sakData.rettshjelp_innbo_selskap_id;
      if (harRettshjelp) {
        setRettshjelp({
          bkf_selskap_navn: sakData.rettshjelp_bkf_selskap_navn,
          bkf_polisenummer: sakData.rettshjelp_bkf_polisenummer,
          bkf_timesats: sakData.rettshjelp_bkf_timesats,
          bkf_tillegg: sakData.rettshjelp_bkf_tillegg,
          bkf_maks_belop: sakData.rettshjelp_bkf_maks_belop,
          innbo_selskap_navn: sakData.rettshjelp_innbo_selskap_navn,
          innbo_polisenummer: sakData.rettshjelp_innbo_polisenummer,
          innbo_dekningsgrad: sakData.rettshjelp_innbo_dekningsgrad,
          innbo_maks_belop: sakData.rettshjelp_innbo_maks_belop,
          innbo_aktiveringsgebyr: sakData.rettshjelp_innbo_aktiveringsgebyr
        });
      } else {
        setRettshjelp(null);
      }

      // Finn forsikring for denne saken
      const sakForsikring = forsikringData.dekninger?.find(
        d => d.sak_id === sakId.toString() || d.sak_id === `${sakId}.0`
      );
      setForsikring(sakForsikring || null);
    } catch (error) {
      console.error('Feil ved lasting av sak:', error);
      console.error('Error stack:', error.stack);
      console.error('SakId:', sakId);
      alert('Kunne ikke laste sak: ' + error.message + '\nSe konsoll for detaljer.');
      // Set empty data to prevent crashes
      setSak(null);
      setFiler([]);
      setTidslinje([]);
      setFrister([]);
      setFakturaer([]);
    } finally {
      setLoading(false);
    }
  };

  const handleFilOpplasting = async (event) => {
    const fil = event.target.files[0];
    if (!fil) return;

    try {
      setUploadingFile(true);

      await crmApi.uploadFil(sakId, fil, 'dokumenter');

      // Last filer på nytt
      const nyeFiler = await crmApi.getSakFiler(sakId);
      setFiler(nyeFiler);

      alert('Fil lastet opp!');
    } catch (error) {
      console.error('Feil ved opplasting:', error);
      alert('Kunne ikke laste opp fil. Se konsoll for detaljer.');
    } finally {
      setUploadingFile(false);
      event.target.value = '';
    }
  };

  const handleApneFil = async (fil) => {
    try {
      const { downloadUrl } = await crmApi.getDownloadUrl(fil.id, fil.parentReference.driveId);
      window.open(downloadUrl, '_blank');
    } catch (error) {
      console.error('Feil ved åpning av fil:', error);
      alert('Kunne ikke åpne fil');
    }
  };

  const handleSlettFil = async (fil) => {
    // eslint-disable-next-line no-restricted-globals
    if (!confirm(`Er du sikker på at du vil slette "${fil.name}"?`)) return;

    try {
      await crmApi.slettFil(fil.id, sakId);

      // Last filer på nytt
      const nyeFiler = await crmApi.getSakFiler(sakId);
      setFiler(nyeFiler);

      alert('Fil slettet!');
    } catch (error) {
      console.error('Feil ved sletting:', error);
      alert('Kunne ikke slette fil');
    }
  };

  const handleApneSharePoint = () => {
    if (sak?.sharepoint_web_url) {
      window.open(sak.sharepoint_web_url, '_blank');
    }
  };

  const handleTilbake = () => {
    window.location.href = '/saker';
  };

  const handleRedigerSak = () => {
    setEditForm({
      tittel: sak.tittel || '',
      beskrivelse: sak.beskrivelse || '',
      sakstype: sak.sakstype || '',
      status: sak.status || 'aktiv',
      timesats: sak.timesats || '',
      klient_navn: sak.klient_navn || '',
      klient_telefon: sak.klient_telefon || '',
      klient_epost: sak.klient_epost || '',
      klient_adresse: sak.klient_adresse || '',
      neste_oppfolging_klient_dato: sak.neste_oppfolging_klient_dato || '',
      neste_oppfolging_klient_type: sak.neste_oppfolging_klient_type || '',
      neste_oppfolging_klient_beskrivelse: sak.neste_oppfolging_klient_beskrivelse || '',
      neste_oppfolging_motpart_dato: sak.neste_oppfolging_motpart_dato || '',
      neste_oppfolging_motpart_type: sak.neste_oppfolging_motpart_type || '',
      neste_oppfolging_motpart_beskrivelse: sak.neste_oppfolging_motpart_beskrivelse || ''
    });
    setShowEditModal(true);
  };

  const handleLagreEndringer = async () => {
    try {
      // Separer klientdata fra sakdata
      const sakData = {
        tittel: editForm.tittel,
        beskrivelse: editForm.beskrivelse || editForm.saksbeskrivelse,
        sakstype: editForm.sakstype,
        status: editForm.status,
        timesats: editForm.timesats ? parseFloat(editForm.timesats) : null,
        saksbeskrivelse: editForm.beskrivelse || editForm.saksbeskrivelse
      };

      const klientData = {
        navn: editForm.klient_navn,
        telefon: editForm.klient_telefon,
        epost: editForm.klient_epost,
        adresse: editForm.klient_adresse
      };

      // Oppdater sak
      await crmApi.updateSak(sakId, sakData);

      // Oppdater klient hvis klient_id finnes
      if (sak.klient_id) {
        await fetch(`${crmApi.baseUrl}/api/klienter/${sak.klient_id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(klientData)
        });
      }

      // Refresh sakdata
      await lastSakData();
      setShowEditModal(false);
      alert('Saken og klientopplysninger ble oppdatert!');
    } catch (error) {
      console.error('Feil ved oppdatering:', error);
      alert('Kunne ikke oppdatere sak. Se konsoll for detaljer.');
    }
  };

  const handleSlettSak = async () => {
    // eslint-disable-next-line no-restricted-globals
    if (!confirm(`Er du sikker på at du vil slette saken "${sak.saksnummer}"? Dette kan ikke angres.`)) return;

    try {
      await crmApi.deleteSak(sakId);
      alert('Saken ble slettet!');
      window.location.href = '/saker';
    } catch (error) {
      console.error('Feil ved sletting:', error);
      alert('Kunne ikke slette sak. Se konsoll for detaljer.');
    }
  };

  const handlePhaseUpdate = async (newPhaseId) => {
    try {
      // Oppdater completed_phases basert på ny fase
      const completedPhases = [];
      for (let i = 1; i < newPhaseId; i++) {
        completedPhases.push(i);
      }

      await crmApi.updateSak(sakId, {
        current_phase_id: newPhaseId,
        completed_phases: completedPhases
      });

      // Oppdater lokal state
      setSak({
        ...sak,
        current_phase_id: newPhaseId,
        completed_phases: completedPhases
      });
    } catch (error) {
      console.error('Feil ved oppdatering av fase:', error);
      alert('Kunne ikke oppdatere fase. Se konsoll for detaljer.');
    }
  };

  const handleGenererOppdragsbekreftelse = async () => {
    try {
      setGenererOppdragsbekreftelseLoading(true);

      const token = localStorage.getItem('access_token');
      const response = await fetch(`${crmApi.baseUrl}/api/oppdragsbekreftelse/generer`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ sak_id: sakId })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Feil ved generering av oppdragsbekreftelse');
      }

      const result = await response.json();

      // Åpne HTML i ny fane for nedlasting/utskrift
      const htmlWindow = window.open('', '_blank');
      htmlWindow.document.write(result.data.html);
      htmlWindow.document.close();

      // Brukeren kan nå velge å skrive ut til PDF eller laste ned
      setTimeout(() => {
        htmlWindow.print();
      }, 500);

      setVisOppdragsbekreftelseModal(false);
      // Oppdater dokumenthistorikk
      lastDokumentHistorikk();
    } catch (error) {
      console.error('Feil ved generering av oppdragsbekreftelse:', error);
      alert(`Kunne ikke generere oppdragsbekreftelse: ${error.message}`);
    } finally {
      setGenererOppdragsbekreftelseLoading(false);
    }
  };

  const handleLastNedOppdragsbekreftelsePDF = async () => {
    try {
      setGenererOppdragsbekreftelseLoading(true);

      const token = localStorage.getItem('access_token');
      console.log('🔑 Token check:', {
        hasToken: !!token,
        tokenPreview: token ? token.substring(0, 30) + '...' : 'MISSING',
        sakId: sakId,
        url: `${crmApi.baseUrl}/api/oppdragsbekreftelse/generer`
      });

      const response = await fetch(`${crmApi.baseUrl}/api/oppdragsbekreftelse/generer`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ sak_id: sakId })
      });

      console.log('📡 Response:', {
        status: response.status,
        ok: response.ok
      });

      if (!response.ok) {
        const error = await response.json();
        console.error('❌ Error response:', error);
        throw new Error(error.error || 'Feil ved generering av oppdragsbekreftelse');
      }

      const result = await response.json();

      // Generer PDF direkte fra HTML
      const filename = `oppdragsbekreftelse_${sak.saksnummer || sakId}_${new Date().toISOString().split('T')[0]}`;
      await genererPDFFraHTMLString(result.data.html, filename);

      setVisOppdragsbekreftelseModal(false);
      // Oppdater dokumenthistorikk
      lastDokumentHistorikk();
      alert('✅ PDF generert og lagret!');
    } catch (error) {
      console.error('Feil ved PDF-generering:', error);
      alert(`Kunne ikke generere PDF: ${error.message}`);
    } finally {
      setGenererOppdragsbekreftelseLoading(false);
    }
  };

  const handleLastOppOppdragsbekreftelse = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      setOpplastingOppdragsbekreftelse(true);

      // Les filen som base64
      const reader = new FileReader();
      reader.onload = async (event) => {
        try {
          const base64Data = event.target.result;

          const token = localStorage.getItem('access_token');
          const response = await fetch(`${crmApi.baseUrl}/api/oppdragsbekreftelse/last-opp`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({
              sak_id: sakId,
              fil_data: base64Data,
              fil_navn: file.name,
              fil_type: file.type
            })
          });

          if (!response.ok) {
            const error = await response.json();
            throw new Error(error.error || 'Feil ved opplasting');
          }

          alert('✅ Oppdragsbekreftelse lastet opp!');
          setVisOppdragsbekreftelseModal(false);
          lastDokumentHistorikk();
        } catch (error) {
          console.error('Feil ved opplasting:', error);
          alert(`Kunne ikke laste opp oppdragsbekreftelse: ${error.message}`);
        } finally {
          setOpplastingOppdragsbekreftelse(false);
        }
      };

      reader.readAsDataURL(file);
    } catch (error) {
      console.error('Feil ved lesing av fil:', error);
      alert('Kunne ikke lese filen');
      setOpplastingOppdragsbekreftelse(false);
    }
  };

  const handleGenererStatusrapport = async () => {
    try {
      setGenererStatusrapportLoading(true);

      const token = localStorage.getItem('access_token');
      const response = await fetch(`${crmApi.baseUrl}/api/klientrapport/generer`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ sak_id: sakId })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Feil ved generering av statusrapport');
      }

      const result = await response.json();

      // Åpne HTML i ny fane for nedlasting/utskrift
      const htmlWindow = window.open('', '_blank');
      htmlWindow.document.write(result.data.html);
      htmlWindow.document.close();

      // Brukeren kan nå velge å skrive ut til PDF eller laste ned
      setTimeout(() => {
        htmlWindow.print();
      }, 500);

      setVisStatusrapportModal(false);
    } catch (error) {
      console.error('Feil ved generering av statusrapport:', error);
      alert(`Kunne ikke generere statusrapport: ${error.message}`);
    } finally {
      setGenererStatusrapportLoading(false);
    }
  };

  const handleStatusEndring = async () => {
    try {
      const { nyStatus, avslutningsgrunn, avsluttingsdato, kommentar, genererAvslutningsbrev, initierSluttoppgjor } = statusEndringForm;

      if (!nyStatus) {
        alert('Du må velge en ny status');
        return;
      }

      if (nyStatus === 'avsluttet' && !avslutningsgrunn) {
        alert('Du må velge en avslutningsgrunn når du avslutter saken');
        return;
      }

      // Oppdater saksstatus
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${crmApi.baseUrl}/api/saker/${sakId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          status: nyStatus,
          avsluttet_dato: nyStatus === 'avsluttet' ? avsluttingsdato : null,
          avslutningsgrunn: nyStatus === 'avsluttet' ? avslutningsgrunn : null,
          avslutningskommentar: kommentar
        })
      });

      if (!response.ok) {
        throw new Error('Feil ved oppdatering av status');
      }

      // Generer avslutningsbrev hvis ønsket
      if (genererAvslutningsbrev && nyStatus === 'avsluttet') {
        try {
          const brevResponse = await fetch(`${crmApi.baseUrl}/api/avslutningsbrev/generer`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({
              sak_id: sakId,
              avslutningsgrunn,
              avsluttingsdato,
              kommentar
            })
          });

          if (brevResponse.ok) {
            const brevResult = await brevResponse.json();
            // Åpne brevet i ny fane
            const htmlWindow = window.open('', '_blank');
            htmlWindow.document.write(brevResult.data.html);
            htmlWindow.document.close();
          }
        } catch (error) {
          console.error('Feil ved generering av avslutningsbrev:', error);
          alert('Statusen ble oppdatert, men avslutningsbrevet kunne ikke genereres');
        }
      }

      // Last inn saken på nytt
      await lastSakData();

      // Lukk modalen
      setVisStatusEndringModal(false);

      // Hvis sluttoppgjør er valgt, åpne fakturamodal
      if (initierSluttoppgjor && nyStatus === 'avsluttet') {
        setTimeout(() => {
          setVisFakturaModal(true);
        }, 300);
      }

      alert('Saksstatus oppdatert!');
    } catch (error) {
      console.error('Feil ved statusendring:', error);
      alert(`Kunne ikke oppdatere status: ${error.message}`);
    }
  };

  const handleEksporterTripletexTimer = async () => {
    try {
      setEksportererTripletex(true);

      const token = localStorage.getItem('access_token');
      const response = await fetch(`${crmApi.baseUrl}/api/tripletex/export-timer?sak_id=${sakId}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Feil ved eksport');
      }

      // Last ned CSV-fil
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `tripletex_timer_${sak.saksnummer}_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      alert('✅ Timer eksportert til Tripletex CSV-format');
    } catch (error) {
      console.error('Feil ved Tripletex-eksport:', error);
      alert(`Kunne ikke eksportere timer: ${error.message}`);
    } finally {
      setEksportererTripletex(false);
    }
  };

  const handleEksporterTripletexEstimat = async () => {
    try {
      setEksportererTripletex(true);

      const token = localStorage.getItem('access_token');
      const response = await fetch(`${crmApi.baseUrl}/api/tripletex/export-sak-estimat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ sak_id: sakId })
      });

      if (!response.ok) {
        throw new Error('Feil ved eksport');
      }

      // Last ned CSV-fil
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `tripletex_estimat_${sak.saksnummer}_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      alert('✅ Saksestimat eksportert til Tripletex CSV-format');
    } catch (error) {
      console.error('Feil ved Tripletex-eksport:', error);
      alert(`Kunne ikke eksportere estimat: ${error.message}`);
    } finally {
      setEksportererTripletex(false);
    }
  };

  const lastBudsjettStatus = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${crmApi.baseUrl}/api/budsjett/status`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) return;

      const result = await response.json();

      // Finn denne saken i resultatene
      const alleSaker = [...result.saker_kritisk, ...result.saker_varsling, ...result.saker_ok];
      const denneSak = alleSaker.find(s => s.sak_id === sakId || s.sak_id === parseInt(sakId));

      if (denneSak) {
        setBudsjettStatus(denneSak);
      }
    } catch (error) {
      console.error('Feil ved lasting av budsjett-status:', error);
    }
  };

  // Last budsjett-status når sak lastes
  useEffect(() => {
    if (sakId && sak) {
      lastBudsjettStatus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sakId, sak]);

  if (loading) {
    return (
      <div className="sak-detaljer loading">
        <div className="spinner"></div>
        <p>Laster sak...</p>
      </div>
    );
  }

  if (!sak) {
    return (
      <div className="sak-detaljer error">
        <p>Sak ikke funnet</p>
        <button className="btn-primary" onClick={handleTilbake}>
          Tilbake til oversikt
        </button>
      </div>
    );
  }

  return (
    <div className="sak-detaljer">
      {/* Header */}
      <div className="sak-header">
        <button className="btn-back" onClick={handleTilbake}>
          ← Tilbake
        </button>

        <div className="sak-title-section">
          <h1>{sak.saksnummer}</h1>
          {sak.tripletex_prosjektnummer && (
            <p style={{fontSize: '0.9rem', opacity: 0.7, margin: '0.2rem 0'}}>
              Tripletex: {sak.tripletex_prosjektnummer}
            </p>
          )}
          <p className="sak-tittel">{sak.tittel || 'Ingen tittel'}</p>
        </div>

        <div className="sak-actions">
          <button className="btn-primary" onClick={() => setVisFakturaModal(true)}>
            📄 Registrer faktura
          </button>
          <button className="btn-primary" onClick={() => setVisOppdragsbekreftelseModal(true)}>
            📋 Oppdragsbekreftelse
          </button>
          <button className="btn-primary" onClick={() => setVisStatusrapportModal(true)}>
            📊 Statusrapport
          </button>
          <button
            className="btn-tripletex"
            onClick={handleEksporterTripletexTimer}
            disabled={eksportererTripletex}
            title="Eksporter timeføring til Tripletex CSV"
          >
            {eksportererTripletex ? '⏳ Eksporterer...' : '📊 Tripletex Timer'}
          </button>
          <button
            className="btn-tripletex"
            onClick={handleEksporterTripletexEstimat}
            disabled={eksportererTripletex}
            title="Eksporter budsjettestimat til Tripletex CSV"
          >
            {eksportererTripletex ? '⏳ Eksporterer...' : '💰 Tripletex Estimat'}
          </button>
          <button className="btn-secondary" onClick={handleRedigerSak}>
            ✏️ Rediger
          </button>
          <button className="btn-secondary" onClick={handleApneSharePoint}>
            📂 Åpne i SharePoint
          </button>
          <button className="btn-danger" onClick={handleSlettSak}>
            🗑️ Slett
          </button>
          <button
            className={`status-badge status-${sak.status}`}
            onClick={() => {
              setStatusEndringForm(prev => ({ ...prev, nyStatus: sak.status }));
              setVisStatusEndringModal(true);
            }}
            style={{ cursor: 'pointer', border: 'none', padding: '0.5rem 1rem' }}
            title="Klikk for å endre status"
          >
            {sak.status}
          </button>
        </div>
      </div>

      {/* Info Grid */}
      <div className="sak-info-grid">
        <div className="info-card" style={{gridColumn: '1 / -1'}}>
          <label>👤 Klient</label>
          <p style={{fontWeight: 'bold', fontSize: '1.1rem'}}>{sak.klient_navn}</p>
          {sak.klient_telefon && <p style={{fontSize: '0.9rem', color: '#666', marginTop: '0.3rem'}}>📞 {sak.klient_telefon}</p>}
          {sak.klient_epost && <p style={{fontSize: '0.9rem', color: '#666', marginTop: '0.2rem'}}>✉️ {sak.klient_epost}</p>}
          {sak.klient_adresse && <p style={{fontSize: '0.9rem', color: '#666', marginTop: '0.2rem'}}>📍 {sak.klient_adresse}</p>}
        </div>

        <div className="info-card">
          <label>Sakstype</label>
          <p>{sak.sakstype || '-'}</p>
        </div>

        <div className="info-card">
          <label>Opprettet</label>
          <p>{formatDato(sak.opprettet_dato)}</p>
        </div>

        <div className="info-card">
          <label>Sist endret</label>
          <p>{formatDato(sak.sist_endret)}</p>
        </div>

        <div className="info-card">
          <label>Timesats</label>
          <p>{sak.timesats ? `${sak.timesats} kr (+ mva)` : 'Standard timesats'}</p>
        </div>
      </div>

      {/* Rettshjelp/Forsikring Status Banner */}
      {(rettshjelp || forsikring) && (
        <div style={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          padding: '1rem',
          borderRadius: '8px',
          marginBottom: '1rem',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div>
            <strong style={{fontSize: '1.1rem'}}>🛡️ Rettshjelpsdekning aktiv</strong>
            {rettshjelp && (
              <p style={{margin: '0.3rem 0 0 0', fontSize: '0.9rem', opacity: 0.9}}>
                {rettshjelp.forsikringsselskap} • {rettshjelp.forsikringstype}
                {rettshjelp.forsikring_saksnummer && ` • Saksnr: ${rettshjelp.forsikring_saksnummer}`}
              </p>
            )}
            {forsikring && !rettshjelp && (
              <p style={{margin: '0.3rem 0 0 0', fontSize: '0.9rem', opacity: 0.9}}>
                {forsikring.forsikringsselskap} • {forsikring.forsikringstype}
              </p>
            )}
          </div>
          <button
            onClick={() => setActiveTab('rettshjelp')}
            style={{
              background: 'rgba(255,255,255,0.2)',
              border: '1px solid rgba(255,255,255,0.3)',
              color: 'white',
              padding: '0.5rem 1rem',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '0.9rem'
            }}
          >
            Se detaljer →
          </button>
        </div>
      )}

      {/* Budsjett-status Banner */}
      {budsjettStatus && (
        <div style={{
          background: budsjettStatus.budsjett_prosent >= 100
            ? 'linear-gradient(135deg, #dc3545 0%, #c82333 100%)'
            : budsjettStatus.budsjett_prosent >= 80
            ? 'linear-gradient(135deg, #ffc107 0%, #e0a800 100%)'
            : 'linear-gradient(135deg, #28a745 0%, #218838 100%)',
          color: 'white',
          padding: '1rem',
          borderRadius: '8px',
          marginBottom: '1rem'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
            <div>
              <strong style={{fontSize: '1.1rem'}}>
                {budsjettStatus.budsjett_prosent >= 100 ? '🔴 KRITISK' : budsjettStatus.budsjett_prosent >= 80 ? '🟡 VARSEL' : '✅ OK'} - Budsjettforbruk
              </strong>
              <p style={{margin: '0.3rem 0 0 0', fontSize: '0.9rem', opacity: 0.9}}>
                {budsjettStatus.budsjett_prosent}% brukt •
                {budsjettStatus.gjenvaerende_timer > 0
                  ? ` ${budsjettStatus.gjenvaerende_timer.toFixed(1)}t gjenstår`
                  : ` ${Math.abs(budsjettStatus.gjenvaerende_timer).toFixed(1)}t over budsjett`
                }
              </p>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: '0.85rem', opacity: 0.9 }}>
                Estimert: {budsjettStatus.total_estimert}t
              </div>
              <div style={{ fontSize: '0.85rem', opacity: 0.9 }}>
                Brukt: {budsjettStatus.total_faktisk}t
              </div>
            </div>
          </div>
          <div style={{ background: 'rgba(255,255,255,0.2)', borderRadius: '12px', height: '24px', overflow: 'hidden' }}>
            <div style={{
              background: 'rgba(255,255,255,0.9)',
              height: '100%',
              width: `${Math.min(budsjettStatus.budsjett_prosent, 100)}%`,
              transition: 'width 0.3s ease',
              borderRadius: '12px'
            }}></div>
          </div>
        </div>
      )}

      {/* Oppfølging Banner */}
      {sak.status === 'aktiv' && (
        <div style={{
          background: (!sak.neste_oppfolging_klient_dato && !sak.neste_oppfolging_motpart_dato)
            ? 'linear-gradient(135deg, #ff6b6b 0%, #ee5a52 100%)'
            : 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
          color: 'white',
          padding: '1rem',
          borderRadius: '8px',
          marginBottom: '1rem'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ flex: 1 }}>
              {(!sak.neste_oppfolging_klient_dato && !sak.neste_oppfolging_motpart_dato) ? (
                <>
                  <strong style={{fontSize: '1.1rem'}}>⚠️ Ingen oppfølging registrert</strong>
                  <p style={{margin: '0.3rem 0 0 0', fontSize: '0.9rem', opacity: 0.9}}>
                    Registrer neste oppfølging med klient og motpart for å holde oversikt
                  </p>
                </>
              ) : (
                <>
                  <strong style={{fontSize: '1.1rem'}}>📞 Neste oppfølging</strong>
                  <div style={{marginTop: '0.5rem', display: 'flex', gap: '2rem', flexWrap: 'wrap'}}>
                    {sak.neste_oppfolging_klient_dato && (
                      <div>
                        <div style={{fontSize: '0.85rem', opacity: 0.8}}>Klient:</div>
                        <div style={{fontWeight: 'bold'}}>
                          {formatDato(sak.neste_oppfolging_klient_dato)}
                          {sak.neste_oppfolging_klient_type && ` - ${sak.neste_oppfolging_klient_type}`}
                        </div>
                        {sak.neste_oppfolging_klient_beskrivelse && (
                          <div style={{fontSize: '0.85rem', marginTop: '0.2rem', opacity: 0.9}}>
                            {sak.neste_oppfolging_klient_beskrivelse}
                          </div>
                        )}
                      </div>
                    )}
                    {sak.neste_oppfolging_motpart_dato && (
                      <div>
                        <div style={{fontSize: '0.85rem', opacity: 0.8}}>Motpart:</div>
                        <div style={{fontWeight: 'bold'}}>
                          {formatDato(sak.neste_oppfolging_motpart_dato)}
                          {sak.neste_oppfolging_motpart_type && ` - ${sak.neste_oppfolging_motpart_type}`}
                        </div>
                        {sak.neste_oppfolging_motpart_beskrivelse && (
                          <div style={{fontSize: '0.85rem', marginTop: '0.2rem', opacity: 0.9}}>
                            {sak.neste_oppfolging_motpart_beskrivelse}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
            <button
              onClick={() => setActiveTab('oppfolging')}
              style={{
                background: 'rgba(255,255,255,0.2)',
                border: '1px solid rgba(255,255,255,0.3)',
                color: 'white',
                padding: '0.5rem 1rem',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '0.9rem',
                whiteSpace: 'nowrap'
              }}
            >
              {(!sak.neste_oppfolging_klient_dato && !sak.neste_oppfolging_motpart_dato) ? 'Registrer oppfølging' : 'Rediger oppfølging'} →
            </button>
          </div>
        </div>
      )}

      {/* Beskrivelse */}
      {sak.beskrivelse && (
        <div className="sak-beskrivelse">
          <h3>Beskrivelse</h3>
          <p>{sak.beskrivelse}</p>
        </div>
      )}

      {/* Quick Actions */}
      <div style={{
        display: 'flex',
        gap: '0.75rem',
        marginBottom: '1.5rem',
        flexWrap: 'wrap',
        padding: '1rem',
        background: '#f8f9fa',
        borderRadius: '8px',
        border: '1px solid #dee2e6'
      }}>
        <h3 style={{width: '100%', margin: '0 0 0.5rem 0', fontSize: '0.95rem', color: '#495057', fontWeight: '600'}}>
          ⚡ Hurtighandlinger
        </h3>
        <button
          className="btn-secondary"
          onClick={() => setActiveTab('oppgaver')}
          style={{
            padding: '0.5rem 1rem',
            fontSize: '0.9rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}
        >
          ➕ Ny oppgave
        </button>
        <button
          className="btn-secondary"
          onClick={() => setActiveTab('frister')}
          style={{
            padding: '0.5rem 1rem',
            fontSize: '0.9rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}
        >
          📅 Legg til frist
        </button>
        <button
          className="btn-secondary"
          onClick={() => setActiveTab('epost')}
          style={{
            padding: '0.5rem 1rem',
            fontSize: '0.9rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}
        >
          ✉️ Send e-post
        </button>
        <button
          className="btn-secondary"
          onClick={() => setActiveTab('filer')}
          style={{
            padding: '0.5rem 1rem',
            fontSize: '0.9rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}
        >
          📎 Last opp fil
        </button>
        <button
          className="btn-secondary"
          onClick={() => setShowEditModal(true)}
          style={{
            padding: '0.5rem 1rem',
            fontSize: '0.9rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}
        >
          📞 Oppdater oppfølging
        </button>
        <button
          className="btn-secondary"
          onClick={() => setActiveTab('tidslinje')}
          style={{
            padding: '0.5rem 1rem',
            fontSize: '0.9rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}
        >
          📝 Legg til notat
        </button>
        {sak.klient_epost && (
          <button
            className="btn-secondary"
            onClick={() => window.location.href = `mailto:${sak.klient_epost}`}
            style={{
              padding: '0.5rem 1rem',
              fontSize: '0.9rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}
          >
            📧 {sak.klient_epost}
          </button>
        )}
        {sak.klient_telefon && (
          <button
            className="btn-secondary"
            onClick={() => window.location.href = `tel:${sak.klient_telefon}`}
            style={{
              padding: '0.5rem 1rem',
              fontSize: '0.9rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}
          >
            📞 {sak.klient_telefon}
          </button>
        )}
      </div>

      {/* Tabs */}
      <div className="sak-tabs">
        <button
          className={activeTab === 'tidslinje' ? 'active' : ''}
          onClick={() => setActiveTab('tidslinje')}
        >
          📝 Tidslinje ({(tidslinje || []).length})
        </button>
        <button
          className={activeTab === 'filer' ? 'active' : ''}
          onClick={() => setActiveTab('filer')}
        >
          📎 Filer ({(filer || []).length})
        </button>
        <button
          className={activeTab === 'bevis' ? 'active' : ''}
          onClick={() => setActiveTab('bevis')}
        >
          🔒 Bevis
        </button>
        <button
          className={activeTab === 'mangler' ? 'active' : ''}
          onClick={() => setActiveTab('mangler')}
        >
          📋 Mangler
        </button>
        <button
          className={activeTab === 'frister' ? 'active' : ''}
          onClick={() => setActiveTab('frister')}
        >
          ⏰ Frister ({(frister || []).length})
        </button>
        <button
          className={activeTab === 'rettshjelp' ? 'active' : ''}
          onClick={() => setActiveTab('rettshjelp')}
        >
          ⚖️ Rettshjelp {rettshjelp ? '✓' : ''}
        </button>
        <button
          className={activeTab === 'fakturaer' ? 'active' : ''}
          onClick={() => setActiveTab('fakturaer')}
        >
          💰 Fakturafordeling ({(fakturaer || []).length})
        </button>
        <button
          className={activeTab === 'forsikring' ? 'active' : ''}
          onClick={() => setActiveTab('forsikring')}
        >
          🛡️ Forsikring {forsikring ? '✓' : ''}
        </button>
        <button
          className={activeTab === 'oppgaver' ? 'active' : ''}
          onClick={() => setActiveTab('oppgaver')}
        >
          📋 Oppgaver
        </button>
        <button
          className={activeTab === 'oppgaver-gruppert' ? 'active' : ''}
          onClick={() => setActiveTab('oppgaver-gruppert')}
        >
          📋 Oppgaver (gruppert)
        </button>
        <button
          className={activeTab === 'oppfolging' ? 'active' : ''}
          onClick={() => setActiveTab('oppfolging')}
        >
          📞 Oppfølging {(!sak.neste_oppfolging_klient_dato && !sak.neste_oppfolging_motpart_dato) ? '⚠️' : ''}
        </button>
        <button
          className={activeTab === 'epost' ? 'active' : ''}
          onClick={() => setActiveTab('epost')}
        >
          📧 E-post
        </button>
        <button
          className={activeTab === 'todos' ? 'active' : ''}
          onClick={() => setActiveTab('todos')}
        >
          ✅ TODO/Microsoft
        </button>
        <button
          className={activeTab === 'dokumenter' ? 'active' : ''}
          onClick={() => setActiveTab('dokumenter')}
        >
          📄 Dokumenter ({dokumentHistorikk.length})
        </button>
        <button
          className={activeTab === 'aktivitetslogg' ? 'active' : ''}
          onClick={() => setActiveTab('aktivitetslogg')}
        >
          📋 Aktivitetslogg ({auditLogs.length})
        </button>
      </div>

      {/* Tab Content */}
      <div className="tab-content">
        {activeTab === 'tidslinje' && sak && (
          <SaksTimeline
            sakId={sakId}
            currentPhaseId={sak.current_phase_id || 1}
            completedPhases={sak.completed_phases || []}
            onPhaseUpdate={handlePhaseUpdate}
          />
        )}

        {activeTab === 'filer' && (
          <div className="filer-section">
            <div className="filer-header">
              <h3>Dokumenter</h3>
              <label className="btn-upload">
                {uploadingFile ? 'Laster opp...' : '+ Last opp fil'}
                <input
                  type="file"
                  onChange={handleFilOpplasting}
                  disabled={uploadingFile}
                  style={{ display: 'none' }}
                />
              </label>
            </div>

            {(filer || []).length === 0 ? (
              <div className="empty-state">
                <p>Ingen filer ennå</p>
              </div>
            ) : (
              <div className="filer-liste">
                {(filer || []).map(fil => (
                  <div key={fil.id} className="fil-item">
                    <div className="fil-icon">
                      {getFilIkon(fil.name)}
                    </div>
                    <div className="fil-info">
                      <h4>{fil.name}</h4>
                      <p>{formatFilStorrelse(fil.size)} • {formatDato(fil.lastModifiedDateTime)}</p>
                    </div>
                    <div className="fil-actions">
                      <button
                        className="btn-icon"
                        onClick={() => {
                          setAktivtDokument(fil);
                          setVisBevisDialog(true);
                        }}
                        title="Merk som bevis"
                        style={{ fontSize: '1rem' }}
                      >
                        🔒
                      </button>
                      <button
                        className="btn-icon"
                        onClick={() => handleApneFil(fil)}
                        title="Åpne fil"
                      >
                        📂
                      </button>
                      {fil.id && fil.parentReference?.driveId && (
                        <button
                          className="btn-icon"
                          onClick={() => {
                            setValgFilForVersjoner(fil);
                            setVisVersjonerModal(true);
                          }}
                          title="Se versjonshistorikk"
                        >
                          📋
                        </button>
                      )}
                      <button
                        className="btn-icon"
                        onClick={() => handleSlettFil(fil)}
                        title="Slett fil"
                      >
                        🗑️
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'frister' && (
          <div className="frister-section">
            {(frister || []).length === 0 ? (
              <div className="empty-state">
                <p>Ingen frister satt</p>
              </div>
            ) : (
              <div className="frister-liste">
                {(frister || []).map(frist => (
                  <div key={frist.id} className="frist-item">
                    <div className="frist-dato">
                      {formatDato(frist.frist_dato)}
                    </div>
                    <div className="frist-innhold">
                      <h4>{frist.beskrivelse}</h4>
                      {frist.notater && <p>{frist.notater}</p>}
                    </div>
                    <div className={`frist-priority priority-${frist.prioritet}`}>
                      {frist.prioritet}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'rettshjelp' && (
          <div className="rettshjelp-section">
            <div className="section-header">
              <h3>Rettshjelp / Forsikringsdekning</h3>
              <button
                className="btn-primary"
                onClick={() => {
                  setRettshjelpForm(rettshjelp || {});
                  setShowRettshjelpModal(true);
                }}
              >
                {rettshjelp ? '✏️ Rediger dekning' : '➕ Legg til dekning'}
              </button>
            </div>

            {!rettshjelp ? (
              <div className="empty-state">
                <p>Ingen rettshjelp/forsikring registrert</p>
                <p className="hint">Klikk "Legg til dekning" for å registrere forsikringsinformasjon</p>
              </div>
            ) : (
              <div className="rettshjelp-info">
                <div className="info-grid">
                  <div className="info-card">
                    <label>Forsikringstype</label>
                    <p>{rettshjelp.forsikringstype || '-'}</p>
                  </div>
                  <div className="info-card">
                    <label>Forsikringsselskap</label>
                    <p>{rettshjelp.forsikringsselskap || '-'}</p>
                  </div>
                  <div className="info-card">
                    <label>Saksnummer (forsikring)</label>
                    <p>{rettshjelp.forsikring_saksnummer || '-'}</p>
                  </div>
                  <div className="info-card">
                    <label>Dekningssats</label>
                    <p>{rettshjelp.dekningssats_type || '-'} {rettshjelp.dekningssats_beløp ? `- kr ${rettshjelp.dekningssats_beløp}` : ''}</p>
                  </div>
                  <div className="info-card">
                    <label>Egenandel</label>
                    <p>{rettshjelp.egenandel ? `kr ${rettshjelp.egenandel}` : '-'}</p>
                  </div>
                  <div className="info-card">
                    <label>Dekningsgrad</label>
                    <p>{rettshjelp.dekningsgrad_prosent ? `${rettshjelp.dekningsgrad_prosent}%` : '-'}</p>
                  </div>
                  <div className="info-card">
                    <label>Maksbeløp</label>
                    <p>{rettshjelp.maksbeløp ? `kr ${rettshjelp.maksbeløp}` : '-'}</p>
                  </div>
                  <div className="info-card">
                    <label>Dekningsdato fra</label>
                    <p>{rettshjelp.dekningsdato_fra || '-'}</p>
                  </div>
                </div>
                {rettshjelp.notater && (
                  <div className="notater-section">
                    <label>Notater</label>
                    <p>{rettshjelp.notater}</p>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {activeTab === 'fakturaer' && (
          <div className="fakturaer-section">
            <div className="section-header">
              <h3>Fakturafordeling</h3>
              <button className="btn-primary">
                ➕ Last opp faktura
              </button>
            </div>

            {(fakturaer || []).length === 0 ? (
              <div className="empty-state">
                <p>Ingen fakturaer registrert</p>
                <p className="hint">Last opp en faktura for å starte fakturafordeling</p>
              </div>
            ) : (
              <div className="fakturaer-liste">
                {(fakturaer || []).map(faktura => (
                  <div key={faktura.id} className="faktura-item">
                    <div className="faktura-info">
                      <h4>Faktura {faktura.fakturanummer}</h4>
                      <p>Dato: {faktura.fakturadato} • Totalbeløp: kr {faktura.total_belop}</p>
                    </div>
                    <div className="faktura-fordeling">
                      {faktura.bkf_andel > 0 && <span className="andel bkf">BKF: kr {faktura.bkf_andel}</span>}
                      {faktura.innbo_andel > 0 && <span className="andel innbo">Innbo: kr {faktura.innbo_andel}</span>}
                      {faktura.klient_andel > 0 && <span className="andel klient">Klient: kr {faktura.klient_andel}</span>}
                    </div>
                    <span className={`status-badge status-${faktura.status}`}>
                      {faktura.status}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'forsikring' && (
          <div className="forsikring-section">
            {!forsikring ? (
              <div className="empty-state">
                <p>Ingen forsikringsinformasjon registrert for denne saken</p>
                <p className="hint">Legg til forsikringsdekning for å aktivere automatisk fakturafordeling</p>
              </div>
            ) : (
              <div className="forsikring-info">
                {forsikring.bkf_aktiv === 1 && (
                  <div className="forsikring-card bkf-card">
                    <div className="card-header">
                      <h3>🏠 Boligkjøperforsikring (BKF)</h3>
                      <span className="status-badge status-aktiv">Aktiv</span>
                    </div>
                    <div className="card-body">
                      <div className="info-row">
                        <label>Forsikringsselskap</label>
                        <p><strong>{forsikring.bkf_selskap_navn}</strong></p>
                      </div>
                      <div className="info-row">
                        <label>E-post (faktura)</label>
                        <p>{forsikring.bkf_epost || '-'}</p>
                      </div>
                      <div className="info-row">
                        <label>Timesats</label>
                        <p><strong>kr {forsikring.bkf_timesats}</strong> + kr {forsikring.bkf_tillegg_per_time} tillegg</p>
                      </div>
                      {forsikring.bkf_polisenummer && (
                        <div className="info-row">
                          <label>Polisenummer</label>
                          <p>{forsikring.bkf_polisenummer}</p>
                        </div>
                      )}
                      {forsikring.bkf_kid && (
                        <div className="info-row">
                          <label>KID</label>
                          <p>{forsikring.bkf_kid}</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {forsikring.innbo_aktiv === 1 && (
                  <div className="forsikring-card innbo-card">
                    <div className="card-header">
                      <h3>🏡 Innboforsikring / Rettshjelpsforsikring</h3>
                      <span className="status-badge status-aktiv">Aktiv</span>
                    </div>
                    <div className="card-body">
                      <div className="info-row">
                        <label>Forsikringsselskap</label>
                        <p><strong>{forsikring.innbo_selskap_navn}</strong></p>
                      </div>
                      <div className="info-row">
                        <label>E-post (faktura)</label>
                        <p>{forsikring.innbo_epost || '-'}</p>
                      </div>
                      <div className="info-row">
                        <label>Dekning</label>
                        <p><strong>{(forsikring.innbo_dekningsgrad * 100)}%</strong> opptil <strong>kr {forsikring.innbo_maks_belop}</strong></p>
                      </div>
                      <div className="info-row">
                        <label>Aktiveringsgebyr (egenandel)</label>
                        <p>kr {forsikring.innbo_aktiveringsgebyr || 0}</p>
                      </div>
                      {forsikring.innbo_ikraftsettelsesdato && (
                        <div className="info-row">
                          <label>Ikraftsettelsesdato</label>
                          <p>{forsikring.innbo_ikraftsettelsesdato}</p>
                        </div>
                      )}
                      {forsikring.innbo_polisenummer && (
                        <div className="info-row">
                          <label>Polisenummer / Skadenummer</label>
                          <p>{forsikring.innbo_polisenummer}</p>
                        </div>
                      )}
                      <div className="info-row">
                        <label>Totalt utbetalt</label>
                        <p className="amount-paid">kr {forsikring.totalt_utbetalt_innbo || 0}</p>
                      </div>
                    </div>
                  </div>
                )}

                <div className="forsikring-card klient-card">
                  <div className="card-header">
                    <h3>👤 Klient</h3>
                  </div>
                  <div className="card-body">
                    <div className="info-row">
                      <label>Klientens timesats</label>
                      <p><strong>kr {forsikring.klient_timesats}</strong></p>
                    </div>
                    {forsikring.klient_kontonummer && (
                      <div className="info-row">
                        <label>Kontonummer</label>
                        <p>{forsikring.klient_kontonummer}</p>
                      </div>
                    )}
                    {forsikring.klient_kid && (
                      <div className="info-row">
                        <label>KID</label>
                        <p>{forsikring.klient_kid}</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'oppgaver' && (
          <OppgaveListe sakId={sakId} />
        )}

        {activeTab === 'oppgaver-gruppert' && (
          <OppgaverGruppert sakId={sakId} />
        )}

        {activeTab === 'oppfolging' && (
          <div className="oppfolging-section">
            <h3>📞 Oppfølging av saken</h3>

            <div className="oppfolging-grid" style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem', marginTop: '1.5rem'}}>
              {/* Klient oppfølging */}
              <div className="oppfolging-card" style={{background: 'white', padding: '1.5rem', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)'}}>
                <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem'}}>
                  <h4 style={{margin: 0}}>👤 Oppfølging klient</h4>
                </div>

                <div className="form-group" style={{marginBottom: '1rem'}}>
                  <label style={{display: 'block', fontWeight: 600, marginBottom: '0.5rem'}}>Dato</label>
                  <input
                    type="date"
                    value={editForm.neste_oppfolging_klient_dato || ''}
                    onChange={(e) => setEditForm({...editForm, neste_oppfolging_klient_dato: e.target.value})}
                    style={{width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '6px'}}
                  />
                </div>

                <div className="form-group" style={{marginBottom: '1rem'}}>
                  <label style={{display: 'block', fontWeight: 600, marginBottom: '0.5rem'}}>Type</label>
                  <select
                    value={editForm.neste_oppfolging_klient_type || ''}
                    onChange={(e) => setEditForm({...editForm, neste_oppfolging_klient_type: e.target.value})}
                    style={{width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '6px'}}
                  >
                    <option value="">Velg type</option>
                    <option value="Telefon">Telefon</option>
                    <option value="E-post">E-post</option>
                    <option value="Møte">Møte</option>
                    <option value="Brev">Brev</option>
                    <option value="SMS">SMS</option>
                  </select>
                </div>

                <div className="form-group">
                  <label style={{display: 'block', fontWeight: 600, marginBottom: '0.5rem'}}>Beskrivelse</label>
                  <textarea
                    value={editForm.neste_oppfolging_klient_beskrivelse || ''}
                    onChange={(e) => setEditForm({...editForm, neste_oppfolging_klient_beskrivelse: e.target.value})}
                    placeholder="Hva skal gjøres?"
                    rows={3}
                    style={{width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '6px', fontFamily: 'inherit', resize: 'vertical'}}
                  />
                </div>
              </div>

              {/* Motpart oppfølging */}
              <div className="oppfolging-card" style={{background: 'white', padding: '1.5rem', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)'}}>
                <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem'}}>
                  <h4 style={{margin: 0}}>⚖️ Oppfølging motpart</h4>
                </div>

                <div className="form-group" style={{marginBottom: '1rem'}}>
                  <label style={{display: 'block', fontWeight: 600, marginBottom: '0.5rem'}}>Dato</label>
                  <input
                    type="date"
                    value={editForm.neste_oppfolging_motpart_dato || ''}
                    onChange={(e) => setEditForm({...editForm, neste_oppfolging_motpart_dato: e.target.value})}
                    style={{width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '6px'}}
                  />
                </div>

                <div className="form-group" style={{marginBottom: '1rem'}}>
                  <label style={{display: 'block', fontWeight: 600, marginBottom: '0.5rem'}}>Type</label>
                  <select
                    value={editForm.neste_oppfolging_motpart_type || ''}
                    onChange={(e) => setEditForm({...editForm, neste_oppfolging_motpart_type: e.target.value})}
                    style={{width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '6px'}}
                  >
                    <option value="">Velg type</option>
                    <option value="Telefon">Telefon</option>
                    <option value="E-post">E-post</option>
                    <option value="Møte">Møte</option>
                    <option value="Brev">Brev</option>
                    <option value="SMS">SMS</option>
                    <option value="Frist">Frist</option>
                  </select>
                </div>

                <div className="form-group">
                  <label style={{display: 'block', fontWeight: 600, marginBottom: '0.5rem'}}>Beskrivelse</label>
                  <textarea
                    value={editForm.neste_oppfolging_motpart_beskrivelse || ''}
                    onChange={(e) => setEditForm({...editForm, neste_oppfolging_motpart_beskrivelse: e.target.value})}
                    placeholder="Hva skal gjøres?"
                    rows={3}
                    style={{width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '6px', fontFamily: 'inherit', resize: 'vertical'}}
                  />
                </div>
              </div>
            </div>

            <div style={{marginTop: '1.5rem', textAlign: 'right'}}>
              <button
                className="btn-primary"
                onClick={async () => {
                  try {
                    await crmApi.updateSak(sakId, {
                      neste_oppfolging_klient_dato: editForm.neste_oppfolging_klient_dato || null,
                      neste_oppfolging_klient_type: editForm.neste_oppfolging_klient_type || null,
                      neste_oppfolging_klient_beskrivelse: editForm.neste_oppfolging_klient_beskrivelse || null,
                      neste_oppfolging_motpart_dato: editForm.neste_oppfolging_motpart_dato || null,
                      neste_oppfolging_motpart_type: editForm.neste_oppfolging_motpart_type || null,
                      neste_oppfolging_motpart_beskrivelse: editForm.neste_oppfolging_motpart_beskrivelse || null,
                      sist_oppfolging_sjekket: new Date().toISOString().split('T')[0]
                    });
                    await lastSakData();
                    alert('✅ Oppfølging lagret!');
                  } catch (error) {
                    console.error('Feil ved lagring av oppfølging:', error);
                    alert('Kunne ikke lagre oppfølging');
                  }
                }}
              >
                💾 Lagre oppfølging
              </button>
            </div>

            {sak.sist_oppfolging_sjekket && (
              <div style={{marginTop: '1rem', fontSize: '0.85rem', color: '#666', textAlign: 'center'}}>
                Sist sjekket: {formatDato(sak.sist_oppfolging_sjekket)}
              </div>
            )}
          </div>
        )}

        {activeTab === 'epost' && (
          <EmailListe sakId={sakId} />
        )}

        {activeTab === 'todos' && (
          <TodoListe sakId={sakId} />
        )}

        {activeTab === 'dokumenter' && (
          <div className="dokumenter-section">
            <h3>📄 Genererte dokumenter</h3>
            {lasterDokumentHistorikk ? (
              <p>Laster dokumenter...</p>
            ) : dokumentHistorikk.length === 0 ? (
              <div className="empty-state">
                <p>Ingen dokumenter generert ennå</p>
                <p style={{fontSize: '0.9em', color: '#666'}}>
                  Generer en oppdragsbekreftelse fra knappen over for å komme i gang
                </p>
              </div>
            ) : (
              <div className="dokumenter-liste">
                {dokumentHistorikk.map(dok => (
                  <div key={dok.id} className="dokument-kort">
                    <div className="dokument-header">
                      <h4>
                        {dok.dokumenttype === 'oppdragsbekreftelse' ? '📋 Oppdragsbekreftelse' : dok.dokumenttype}
                      </h4>
                      <span className="dokument-dato">
                        {new Date(dok.generert_dato).toLocaleDateString('nb-NO', {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </span>
                    </div>
                    <div className="dokument-info">
                      <p><strong>Generert av:</strong> {dok.generert_av}</p>
                      {dok.klientnavn && <p><strong>Klient:</strong> {dok.klientnavn}</p>}
                      {dok.sakstype && <p><strong>Sakstype:</strong> {dok.sakstype}</p>}
                      {dok.estimertKostnad && (
                        <p><strong>Estimert kostnad:</strong> {dok.estimertKostnad.toLocaleString('nb-NO')} kr</p>
                      )}
                    </div>
                    <div className="dokument-actions">
                      <button
                        className="btn-secondary btn-sm"
                        onClick={async () => {
                          try {
                            const token = localStorage.getItem('access_token');
                            const response = await fetch(`${crmApi.baseUrl}/api/oppdragsbekreftelse/dokument/${dok.id}`, {
                              headers: { 'Authorization': `Bearer ${token}` }
                            });
                            const result = await response.json();
                            const htmlWindow = window.open('', '_blank');
                            htmlWindow.document.write(result.data.html_innhold);
                            htmlWindow.document.close();
                          } catch (error) {
                            alert('Kunne ikke åpne dokument');
                          }
                        }}
                      >
                        👁️ Forhåndsvis
                      </button>
                      <button
                        className="btn-primary btn-sm"
                        onClick={async () => {
                          try {
                            const token = localStorage.getItem('access_token');
                            const response = await fetch(`${crmApi.baseUrl}/api/oppdragsbekreftelse/dokument/${dok.id}`, {
                              headers: { 'Authorization': `Bearer ${token}` }
                            });
                            const result = await response.json();
                            const filename = `${dok.dokumenttype}_${dok.saksnummer || sakId}_${new Date(dok.generert_dato).toISOString().split('T')[0]}`;
                            await genererPDFFraHTMLString(result.data.html_innhold, filename);
                          } catch (error) {
                            alert('Kunne ikke laste ned PDF');
                          }
                        }}
                      >
                        📥 Last ned PDF
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'aktivitetslogg' && (
          <div className="aktivitetslogg-section">
            <h3>📋 Aktivitetslogg</h3>
            {lasterAuditLogs ? (
              <p>Laster aktivitetslogg...</p>
            ) : auditLogs.length === 0 ? (
              <div className="empty-state">
                <p>Ingen aktiviteter registrert ennå</p>
              </div>
            ) : (
              <div className="audit-timeline">
                {auditLogs.map((log, idx) => {
                  const actionText = getActionText(log.action, log.resource_type);
                  const resourceText = getResourceText(log.resource_type, log.details);

                  return (
                    <div key={log.id || idx} className="audit-entry">
                      <div className="audit-timestamp">
                        {new Date(log.timestamp).toLocaleDateString('nb-NO', {
                          day: '2-digit',
                          month: 'short',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </div>
                      <div className="audit-content">
                        <div className="audit-action">
                          <strong>{log.user.name}</strong> {actionText}
                          {resourceText && <span className="audit-resource"> {resourceText}</span>}
                        </div>
                        {log.details && Object.keys(log.details).length > 0 && (
                          <div className="audit-details">
                            {log.details.old_status && log.details.new_status && (
                              <span>Status: {log.details.old_status} → {log.details.new_status}</span>
                            )}
                            {log.details.beskrivelse && (
                              <span className="audit-description">{log.details.beskrivelse}</span>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {activeTab === 'bevis' && (
          <div className="bevis-section">
            <BevisOversikt sakId={sakId} />

            {visKategoriAdmin && (
              <KategoriAdmin
                sakId={sakId}
                onClose={() => setVisKategoriAdmin(false)}
                onKategorierOppdatert={() => {
                  // BevisOversikt vil automatisk oppdateres ved neste lasting
                }}
              />
            )}
          </div>
        )}

        {activeTab === 'mangler' && (
          <Mangelsregister sakId={sakId} />
        )}
      </div>

      {/* Rediger Modal */}
      {showEditModal && (
        <div className="modal-overlay" onClick={() => setShowEditModal(false)}>
          <div className="modal-content modal-large" onClick={(e) => e.stopPropagation()}>
            <h2>Rediger sak og klient</h2>

            <h3 style={{marginTop: '1rem', marginBottom: '0.5rem', fontSize: '1.1rem', borderBottom: '2px solid #e0e0e0', paddingBottom: '0.3rem'}}>Saksopplysninger</h3>

            <div className="form-group">
              <label>Tittel</label>
              <input
                type="text"
                value={editForm.tittel}
                onChange={(e) => setEditForm({...editForm, tittel: e.target.value})}
                placeholder="Sakens tittel"
              />
            </div>

            <div className="form-group">
              <label>Beskrivelse</label>
              <textarea
                value={editForm.beskrivelse}
                onChange={(e) => setEditForm({...editForm, beskrivelse: e.target.value})}
                placeholder="Beskrivelse av saken"
                rows={4}
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Sakstype</label>
                <input
                  type="text"
                  value={editForm.sakstype}
                  onChange={(e) => setEditForm({...editForm, sakstype: e.target.value})}
                  placeholder="F.eks. Eiendom, Arbeidsrett, etc."
                />
              </div>

              <div className="form-group">
                <label>Status</label>
                <select
                  value={editForm.status}
                  onChange={(e) => setEditForm({...editForm, status: e.target.value})}
                >
                  <option value="innsalg">Innsalg</option>
                  <option value="aktiv">Aktiv</option>
                  <option value="avventer">Avventer</option>
                  <option value="vent">Vent</option>
                  <option value="privat">Privat</option>
                  <option value="arkivert">Arkivert</option>
                  <option value="tapt_innsalg">Tapt innsalg</option>
                </select>
              </div>
            </div>

            <div className="form-group">
              <label>Timesats (ekskl. mva)</label>
              <input
                type="number"
                step="1"
                min="0"
                value={editForm.timesats}
                onChange={(e) => setEditForm({...editForm, timesats: e.target.value})}
                placeholder="F.eks. 1575 - la stå tom for å bruke standard timesats"
              />
              <small style={{color: '#666', fontSize: '0.85rem', marginTop: '0.25rem', display: 'block'}}>
                La stå tom for å bruke standard timesats fra innstillinger. MVA legges til automatisk.
              </small>
            </div>

            <h3 style={{marginTop: '1.5rem', marginBottom: '0.5rem', fontSize: '1.1rem', borderBottom: '2px solid #e0e0e0', paddingBottom: '0.3rem'}}>Klientopplysninger</h3>

            <div className="form-group">
              <label>Klientnavn *</label>
              <input
                type="text"
                value={editForm.klient_navn}
                onChange={(e) => setEditForm({...editForm, klient_navn: e.target.value})}
                placeholder="Navn på klient"
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Telefon</label>
                <input
                  type="tel"
                  value={editForm.klient_telefon}
                  onChange={(e) => setEditForm({...editForm, klient_telefon: e.target.value})}
                  placeholder="+47 xxx xx xxx"
                />
              </div>

              <div className="form-group">
                <label>E-post</label>
                <input
                  type="email"
                  value={editForm.klient_epost}
                  onChange={(e) => setEditForm({...editForm, klient_epost: e.target.value})}
                  placeholder="klient@example.com"
                />
              </div>
            </div>

            <div className="form-group">
              <label>Adresse</label>
              <input
                type="text"
                value={editForm.klient_adresse}
                onChange={(e) => setEditForm({...editForm, klient_adresse: e.target.value})}
                placeholder="Gateadresse, postnummer, poststed"
              />
            </div>

            <div className="modal-actions">
              <button className="btn-secondary" onClick={() => setShowEditModal(false)}>
                Avbryt
              </button>
              <button className="btn-primary" onClick={handleLagreEndringer}>
                💾 Lagre alle endringer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Rettshjelp Modal */}
      {showRettshjelpModal && (
        <div className="modal-overlay" onClick={() => setShowRettshjelpModal(false)}>
          <div className="modal-content modal-large" onClick={(e) => e.stopPropagation()}>
            <h2>{rettshjelp ? 'Rediger' : 'Legg til'} Rettshjelp/Forsikring</h2>

            <div className="form-row">
              <div className="form-group">
                <label>Forsikringstype</label>
                <select
                  value={rettshjelpForm.forsikringstype || ''}
                  onChange={(e) => setRettshjelpForm({...rettshjelpForm, forsikringstype: e.target.value})}
                >
                  <option value="">Velg type</option>
                  <option value="Boligkjøperforsikring">Boligkjøperforsikring</option>
                  <option value="Innboforsikring">Innboforsikring</option>
                  <option value="Rettshjelp">Rettshjelp</option>
                  <option value="Annet">Annet</option>
                </select>
              </div>

              <div className="form-group">
                <label>Forsikringsselskap</label>
                <input
                  type="text"
                  value={rettshjelpForm.forsikringsselskap || ''}
                  onChange={(e) => setRettshjelpForm({...rettshjelpForm, forsikringsselskap: e.target.value})}
                  placeholder="F.eks. Protector, IF"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>E-post (forsikring)</label>
                <input
                  type="email"
                  value={rettshjelpForm.forsikring_epost || ''}
                  onChange={(e) => setRettshjelpForm({...rettshjelpForm, forsikring_epost: e.target.value})}
                  placeholder="faktura@forsikring.no"
                />
              </div>

              <div className="form-group">
                <label>Saksnummer (forsikring)</label>
                <input
                  type="text"
                  value={rettshjelpForm.forsikring_saksnummer || ''}
                  onChange={(e) => setRettshjelpForm({...rettshjelpForm, forsikring_saksnummer: e.target.value})}
                  placeholder="Forsikringsselskapets saksnummer"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Dekningssats type</label>
                <select
                  value={rettshjelpForm.dekningssats_type || ''}
                  onChange={(e) => setRettshjelpForm({...rettshjelpForm, dekningssats_type: e.target.value})}
                >
                  <option value="">Velg type</option>
                  <option value="Fast">Fast beløp</option>
                  <option value="Prosent">Prosent</option>
                </select>
              </div>

              <div className="form-group">
                <label>Dekningssats beløp (kr)</label>
                <input
                  type="number"
                  value={rettshjelpForm.dekningssats_beløp || ''}
                  onChange={(e) => setRettshjelpForm({...rettshjelpForm, dekningssats_beløp: e.target.value})}
                  placeholder="F.eks. 1375"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Egenandel (kr)</label>
                <input
                  type="number"
                  value={rettshjelpForm.egenandel || ''}
                  onChange={(e) => setRettshjelpForm({...rettshjelpForm, egenandel: e.target.value})}
                  placeholder="F.eks. 5000"
                />
              </div>

              <div className="form-group">
                <label>Dekningsgrad (%)</label>
                <input
                  type="number"
                  value={rettshjelpForm.dekningsgrad_prosent || ''}
                  onChange={(e) => setRettshjelpForm({...rettshjelpForm, dekningsgrad_prosent: e.target.value})}
                  placeholder="F.eks. 80"
                  min="0"
                  max="100"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Maksbeløp (kr)</label>
                <input
                  type="number"
                  value={rettshjelpForm.maksbeløp || ''}
                  onChange={(e) => setRettshjelpForm({...rettshjelpForm, maksbeløp: e.target.value})}
                  placeholder="F.eks. 100000"
                />
              </div>

              <div className="form-group">
                <label>Dekningsdato fra</label>
                <input
                  type="date"
                  value={rettshjelpForm.dekningsdato_fra || ''}
                  onChange={(e) => setRettshjelpForm({...rettshjelpForm, dekningsdato_fra: e.target.value})}
                />
              </div>
            </div>

            <div className="form-group">
              <label>Notater</label>
              <textarea
                value={rettshjelpForm.notater || ''}
                onChange={(e) => setRettshjelpForm({...rettshjelpForm, notater: e.target.value})}
                placeholder="Interne notater om forsikringsdekning"
                rows={3}
              />
            </div>

            <div className="modal-actions">
              <button className="btn-secondary" onClick={() => setShowRettshjelpModal(false)}>
                Avbryt
              </button>
              <button className="btn-primary" onClick={async () => {
                try {
                  const method = rettshjelp ? 'PUT' : 'POST';
                  const url = rettshjelp
                    ? `${crmApi.baseUrl}/api/rettshjelp/${rettshjelp.id}`
                    : `${crmApi.baseUrl}/api/saker/${sakId}/rettshjelp`;

                  const response = await fetch(url, {
                    method,
                    headers: {'Content-Type': 'application/json'},
                    body: JSON.stringify(rettshjelpForm)
                  });

                  if (!response.ok) throw new Error('Feil ved lagring');

                  setShowRettshjelpModal(false);
                  lastSakData(); // Reload data
                  alert('Rettshjelp lagret!');
                } catch (error) {
                  console.error('Feil:', error);
                  alert('Kunne ikke lagre rettshjelp');
                }
              }}>
                Lagre
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Fakturaregistrering modal */}
      {visFakturaModal && (
        <FakturaRegistrering
          sakId={sakId}
          onClose={() => setVisFakturaModal(false)}
          onSuccess={(fakturaId) => {
            setVisFakturaModal(false);
            lastSakData(); // Oppdater sakdata
          }}
        />
      )}

      {/* BevisDialog - Merk dokument som bevis */}
      {visBevisDialog && aktivtDokument && (
        <BevisDialog
          sakId={sakId}
          documentId={aktivtDokument.id}
          documentNavn={aktivtDokument.name}
          onClose={() => {
            setVisBevisDialog(false);
            setAktivtDokument(null);
          }}
          onSuccess={(bevisData) => {
            console.log('Bevis opprettet:', bevisData);
            setVisBevisDialog(false);
            setAktivtDokument(null);
            // Oppdater bevis-oversikt hvis vi er på bevis-tab
            if (activeTab === 'bevis') {
              lastSakData();
            }
          }}
        />
      )}

      {/* Oppdragsbekreftelse Modal - Ny versjon med skjema */}
      {visOppdragsbekreftelseModal && (
        <div className="modal-overlay" onClick={() => setVisOppdragsbekreftelseModal(false)}>
          <div className="modal-content modal-large" onClick={(e) => e.stopPropagation()}>
            <OppdragsbekreftelseSkjema
              sakId={sakId}
              saksnummer={sak.saksnummer}
              onLukk={() => setVisOppdragsbekreftelseModal(false)}
            />
          </div>
        </div>
      )}

      {/* Statusrapport Modal */}
      {visStatusrapportModal && (
        <div className="modal-overlay" onClick={() => setVisStatusrapportModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>📊 Generer Statusrapport</h2>
              <button className="close-btn" onClick={() => setVisStatusrapportModal(false)}>×</button>
            </div>

            <div className="modal-body">
              <p>Du er i ferd med å generere en statusrapport for:</p>

              <div className="info-box" style={{margin: '20px 0', padding: '15px', backgroundColor: '#f5f5f5', borderRadius: '8px'}}>
                <p><strong>Saksnummer:</strong> {sak.saksnummer}</p>
                <p><strong>Klient:</strong> {sak.klient_navn}</p>
                <p><strong>Sakstype:</strong> {sak.sakstype || 'Generell juridisk bistand'}</p>
              </div>

              <p style={{fontSize: '0.9em', color: '#666', marginTop: '15px'}}>
                Statusrapporten vil inkludere fremdrift, oppgaver, tidsbruk, kostnader og aktiviteter.
                Rapporten åpnes i en ny fane hvor du kan skrive den ut til PDF eller sende til klient.
              </p>
            </div>

            <div className="modal-actions">
              <button
                className="btn-secondary"
                onClick={() => setVisStatusrapportModal(false)}
                disabled={genererStatusrapportLoading}
              >
                Avbryt
              </button>
              <button
                className="btn-primary"
                onClick={handleGenererStatusrapport}
                disabled={genererStatusrapportLoading}
              >
                {genererStatusrapportLoading ? 'Genererer...' : '📄 Generer rapport'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Statusendring Modal */}
      {visStatusEndringModal && (
        <div className="modal-overlay" onClick={() => setVisStatusEndringModal(false)}>
          <div className="modal-content modal-large" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>🔄 Endre saksstatus</h2>
              <button className="close-btn" onClick={() => setVisStatusEndringModal(false)}>×</button>
            </div>

            <div className="modal-body">
              <div className="form-group">
                <label>Nåværende status</label>
                <div className={`status-badge status-${sak.status}`} style={{display: 'inline-block', padding: '0.5rem 1rem'}}>
                  {sak.status}
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="nyStatus">Ny status *</label>
                <select
                  id="nyStatus"
                  value={statusEndringForm.nyStatus}
                  onChange={(e) => setStatusEndringForm(prev => ({ ...prev, nyStatus: e.target.value }))}
                >
                  <option value="">-- Velg status --</option>
                  <option value="aktiv">Aktiv</option>
                  <option value="venter">Venter på svar</option>
                  <option value="trukket">Trukket</option>
                  <option value="avsluttet">Avsluttet</option>
                  <option value="arkivert">Arkivert</option>
                </select>
              </div>

              {statusEndringForm.nyStatus === 'avsluttet' && (
                <>
                  <div className="form-group">
                    <label htmlFor="avslutningsgrunn">Avslutningsgrunn *</label>
                    <select
                      id="avslutningsgrunn"
                      value={statusEndringForm.avslutningsgrunn}
                      onChange={(e) => setStatusEndringForm(prev => ({ ...prev, avslutningsgrunn: e.target.value }))}
                    >
                      <option value="">-- Velg grunn --</option>
                      <option value="forlik">Forlik oppnådd</option>
                      <option value="dom_vunnet">Dom vunnet</option>
                      <option value="dom_tapt">Dom tapt</option>
                      <option value="dom_delvis">Dom delvis medhold</option>
                      <option value="trukket_klient">Trukket av klient</option>
                      <option value="henlagt">Henlagt</option>
                      <option value="annet">Annet</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label htmlFor="avsluttingsdato">Avsluttingsdato</label>
                    <input
                      type="date"
                      id="avsluttingsdato"
                      value={statusEndringForm.avsluttingsdato}
                      onChange={(e) => setStatusEndringForm(prev => ({ ...prev, avsluttingsdato: e.target.value }))}
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="kommentar">Kommentar / Notater</label>
                    <textarea
                      id="kommentar"
                      rows="4"
                      placeholder="Valgfri kommentar om avslutningen..."
                      value={statusEndringForm.kommentar}
                      onChange={(e) => setStatusEndringForm(prev => ({ ...prev, kommentar: e.target.value }))}
                    />
                  </div>

                  <div className="form-group">
                    <label style={{display: 'flex', alignItems: 'center', cursor: 'pointer'}}>
                      <input
                        type="checkbox"
                        checked={statusEndringForm.genererAvslutningsbrev}
                        onChange={(e) => setStatusEndringForm(prev => ({ ...prev, genererAvslutningsbrev: e.target.checked }))}
                        style={{marginRight: '0.5rem'}}
                      />
                      📄 Generer avslutningsbrev til klient
                    </label>
                    <p style={{fontSize: '0.85em', color: '#666', marginTop: '0.3rem', marginLeft: '1.5rem'}}>
                      Genererer et profesjonelt avslutningsbrev basert på avslutningsgrunn
                    </p>
                  </div>

                  <div className="form-group">
                    <label style={{display: 'flex', alignItems: 'center', cursor: 'pointer'}}>
                      <input
                        type="checkbox"
                        checked={statusEndringForm.initierSluttoppgjor}
                        onChange={(e) => setStatusEndringForm(prev => ({ ...prev, initierSluttoppgjor: e.target.checked }))}
                        style={{marginRight: '0.5rem'}}
                      />
                      💰 Initier sluttoppgjør / fakturering
                    </label>
                    <p style={{fontSize: '0.85em', color: '#666', marginTop: '0.3rem', marginLeft: '1.5rem'}}>
                      Tar deg til fakturaregistrering for å opprette sluttfaktura
                    </p>
                  </div>
                </>
              )}

              {statusEndringForm.nyStatus === 'trukket' && (
                <div className="form-group">
                  <label htmlFor="kommentar">Begrunnelse for tilbaketrekning</label>
                  <textarea
                    id="kommentar"
                    rows="4"
                    placeholder="Hvorfor ble saken trukket?"
                    value={statusEndringForm.kommentar}
                    onChange={(e) => setStatusEndringForm(prev => ({ ...prev, kommentar: e.target.value }))}
                  />
                </div>
              )}
            </div>

            <div className="modal-actions">
              <button
                className="btn-secondary"
                onClick={() => setVisStatusEndringModal(false)}
              >
                Avbryt
              </button>
              <button
                className="btn-primary"
                onClick={handleStatusEndring}
                disabled={!statusEndringForm.nyStatus || (statusEndringForm.nyStatus === 'avsluttet' && !statusEndringForm.avslutningsgrunn)}
              >
                💾 Lagre statusendring
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Dokumentversjoner Modal */}
      {visVersjonerModal && valgFilForVersjoner && (
        <DokumentVersjoner
          fileId={valgFilForVersjoner.id}
          driveId={valgFilForVersjoner.parentReference?.driveId}
          fileName={valgFilForVersjoner.name}
          onClose={() => {
            setVisVersjonerModal(false);
            setValgFilForVersjoner(null);
          }}
        />
      )}
    </div>
  );
}

// Hjelpefunksjoner
function formatDato(datoString) {
  if (!datoString) return '-';
  const dato = new Date(datoString);
  return dato.toLocaleDateString('nb-NO', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
}

function formatDatoTid(datoString) {
  if (!datoString) return '-';
  const dato = new Date(datoString);
  return dato.toLocaleString('nb-NO', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
}

function formatFilStorrelse(bytes) {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
}

function getActionText(action, resourceType) {
  const actionMap = {
    'create': 'opprettet',
    'update': 'oppdaterte',
    'delete': 'slettet',
    'complete': 'fullførte',
    'reopen': 'gjenåpnet',
    'upload': 'lastet opp',
    'download': 'lastet ned'
  };

  return actionMap[action] || action;
}

function getResourceText(resourceType, details) {
  const resourceMap = {
    'case': 'saken',
    'oppgave': details?.beskrivelse ? `oppgave: "${details.beskrivelse}"` : 'en oppgave',
    'document': details?.filnavn ? `dokument: "${details.filnavn}"` : 'et dokument',
    'frist': details?.beskrivelse ? `frist: "${details.beskrivelse}"` : 'en frist',
    'note': 'et notat',
    'email': 'en e-post'
  };

  return resourceMap[resourceType] || resourceType;
}

function getFilIkon(filnavn) {
  const ext = filnavn.split('.').pop().toLowerCase();

  if (['pdf'].includes(ext)) return '📕';
  if (['doc', 'docx'].includes(ext)) return '📘';
  if (['xls', 'xlsx'].includes(ext)) return '📊';
  if (['jpg', 'jpeg', 'png', 'gif'].includes(ext)) return '🖼️';
  if (['zip', 'rar', '7z'].includes(ext)) return '📦';

  return '📄';
}
